import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { TopBar } from "../components/UI";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";

const req = async (path, opts = {}) => {
  const token = localStorage.getItem("wine_route_admin_token");
  const r = await fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...opts,
  });
  if (!r.ok) {
    const err = new Error(await r.text());
    err.status = r.status;
    throw err;
  }
  return r.json();
};

const ROADS = [
  { slug: "vienna-venice", label: "Vienna – Venice" },
  { slug: "ar-valley", label: "Ahr Valley" },
  { slug: "malopolska", label: "Małopolska" },
];

const S = {
  page: { minHeight: "100vh", background: "#F2F5EC" },
  container: { maxWidth: 900, margin: "0 auto", padding: "20px 16px" },
  card: { background: "white", borderRadius: 12, padding: 24, marginBottom: 16, border: "1px solid #C4D0A8" },
  label: { fontSize: 10, fontWeight: 600, color: "#6A7E58", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 4 },
  input: { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #C4D0A8", background: "#EDF1E4", color: "#2C3820", fontSize: 13, boxSizing: "border-box" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  tab: (active) => ({ padding: "8px 16px", borderRadius: 8, border: "none", background: active ? "#8B1A2F" : "transparent", color: active ? "white" : "#5A6E48", cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400 }),
  statCard: (color) => ({ background: "white", borderRadius: 10, padding: "16px 20px", border: `1px solid #C4D0A8`, borderLeft: `3px solid ${color}` }),
};

// ── Stats Tab ──────────────────────────────────────────────────────────────
function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { req("/manage/admin/stats").then(setStats).catch(console.error); }, []);

  if (!stats) return <div style={{ textAlign: "center", padding: 40, color: "#6A7E58" }}>Loading…</div>;

  const items = [
    { label: "Total Wineries", value: stats.total_wineries, color: "#3D4A1A" },
    { label: "Verified", value: stats.verified_wineries, color: "#3D4A1A" },
    { label: "Pending Review", value: stats.pending_review, color: "#886070" },
    { label: "Total Bookings", value: stats.total_bookings, color: "#8B1A2F" },
    { label: "Pending Bookings", value: stats.pending_bookings, color: "#886070" },
    { label: "Approved Bookings", value: stats.approved_bookings, color: "#3D4A1A" },
    { label: "Wine Roads", value: stats.total_roads, color: "#3D4A1A" },
    { label: "Road Reps", value: stats.total_reps, color: "#8B1A2F" },
    { label: "Winery Managers", value: stats.total_managers, color: "#4A5E38" },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
      {items.map(item => (
        <div key={item.label} style={S.statCard(item.color)}>
          <div style={{ fontSize: 28, fontWeight: 700, color: item.color }}>{item.value}</div>
          <div style={{ fontSize: 12, color: "#6A7E58", marginTop: 4 }}>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Road Reps Tab ──────────────────────────────────────────────────────────
function RepsTab() {
  const [reps, setReps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", organization: "", phone: "", road_slug: "vienna-venice" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const loadReps = () => req("/manage/admin/reps").then(setReps).catch(console.error);
  useEffect(() => { loadReps(); }, []);
  const parseErr = (e) => {
    const msg = e?.message ?? "";
    // Only attempt to unwrap a {"error": "..."} envelope if the message
    // actually looks like a JSON object — otherwise plain client-side
    // errors (e.g. "Invalid JSON for name_translations") get mangled by
    // JSON.parse throwing a second, unrelated error that masks the real one.
    if (msg.trim().startsWith("{")) {
      try { return JSON.parse(msg).error || msg; } catch { /* fall through */ }
    }
    return msg;
  };
  const createRep = async () => {
    setError("");
    if (!form.first_name || !form.last_name || !form.email) { setError("Required fields missing."); return; }
    setLoading(true);
    try {
      const r = await req("/auth/create-rep", { method: "POST", body: JSON.stringify(form) });
      setSuccess(r.user);
      setForm({ first_name: "", last_name: "", email: "", organization: "", phone: "", road_slug: "vienna-venice", password: "" });
      setShowForm(false);
      loadReps();
    } catch (e) { setError(parseErr(e)); }
    finally { setLoading(false); }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this representative? They will no longer be able to log in.")) return;
    setError("");
    try { await req(`/manage/admin/reps/${id}/deactivate`, { method: "POST" }); loadReps(); }
    catch (e) { setError(parseErr(e)); }
  };
  const reactivate = async (id) => {
    setError("");
    try { await req(`/manage/admin/reps/${id}/reactivate`, { method: "POST" }); loadReps(); }
    catch (e) { setError(parseErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this representative? This cannot be undone.")) return;
    setError("");
    try { await req(`/manage/admin/reps/${id}`, { method: "DELETE" }); loadReps(); }
    catch (e) { setError(parseErr(e)); }
  };
  return (
    <div>
      {success && <div style={{ background: "rgba(61,74,26,0.1)", border: "1px solid rgba(61,74,26,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#3D4A1A" }}>✓ Created: <strong>{success.full_name}</strong> ({success.email})</div>}
      {error && <div style={{ background: "rgba(139,26,47,0.08)", border: "1px solid rgba(139,26,47,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#8B1A2F" }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#1E2C14" }}>{reps.length} Representative{reps.length !== 1 ? "s" : ""}</span>
        <button onClick={() => { setShowForm(s => !s); setError(""); }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#8B1A2F", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          {showForm ? "✕ Cancel" : "+ Add representative"}
        </button>
      </div>
      {showForm && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E2C14", marginBottom: 14 }}>New Wine Road Representative</div>
          <div style={{ ...S.grid2, marginBottom: 12 }}>
            <div><label style={S.label}>First name *</label><input style={S.input} value={form.first_name} onChange={e => set("first_name", e.target.value)} /></div>
            <div><label style={S.label}>Last name *</label><input style={S.input} value={form.last_name} onChange={e => set("last_name", e.target.value)} /></div>
            <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Email *</label><input style={S.input} type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
            <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Organization</label><input style={S.input} value={form.organization} onChange={e => set("organization", e.target.value)} placeholder="e.g. Ahr Valley Consortium" /></div>
            <div><label style={S.label}>Phone</label><input style={S.input} value={form.phone} onChange={e => set("phone", e.target.value)} /></div>
            <div><label style={S.label}>Wine road *</label><select style={S.input} value={form.road_slug} onChange={e => set("road_slug", e.target.value)}>{ROADS.map(r => <option key={r.slug} value={r.slug}>{r.label}</option>)}</select></div>
          </div>
          <button onClick={createRep} disabled={loading} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: "#3D4A1A", color: "white", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            {loading ? "Creating…" : "Create representative"}
          </button>
        </div>
      )}
      {reps.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#6A7E58", fontSize: 13 }}>No road representatives yet</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #C4D0A8" }}>
            {["Name", "Email", "Organization", "Road", "Status", "Created", ""].map((h, i) => (
              <th key={i} style={{ textAlign: "left", fontSize: 10, color: "#7A8E68", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", paddingBottom: 8 }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{reps.map((r, i) => {
            const active = r.is_active !== false;
            return (
              <tr key={i} style={{ borderBottom: "1px solid #E8EDE0" }}>
                <td style={{ padding: "10px 4px", fontSize: 13, color: "#2C3820", fontWeight: 500 }}>{r.full_name}</td>
                <td style={{ padding: "10px 4px", fontSize: 12, color: "#5A6E48" }}>{r.email}</td>
                <td style={{ padding: "10px 4px", fontSize: 12, color: "#5A6E48" }}>{r.organization || "—"}</td>
                <td style={{ padding: "10px 4px" }}>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 100, background: "rgba(61,74,26,0.12)", color: "#3D4A1A" }}>
                    {ROADS.find(road => road.slug === r.road_slug)?.label || r.road_slug || "—"}
                  </span>
                </td>
                <td style={{ padding: "10px 4px" }}>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 100, background: active ? "rgba(61,74,26,0.12)" : "rgba(139,26,47,0.1)", color: active ? "#3D4A1A" : "#8B1A2F" }}>
                    {active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: "10px 4px", fontSize: 11, color: "#6A7E58" }}>{r.created_at?.slice(0, 10)}</td>
                <td style={{ padding: "10px 0", whiteSpace: "nowrap" }}>
                  {active ? (
                    <button onClick={() => deactivate(r.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#8B1A2F", cursor: "pointer" }}>Deactivate</button>
                  ) : (
                    <>
                      <button onClick={() => reactivate(r.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#3D4A1A", cursor: "pointer", marginRight: 6 }}>Reactivate</button>
                      <button onClick={() => remove(r.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #8B1A2F", background: "transparent", color: "#8B1A2F", cursor: "pointer" }}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}
function AdminsTab({ currentUserId }) {
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const loadAdmins = () => req("/manage/admin/admins").then(setAdmins).catch(console.error);
  useEffect(() => { loadAdmins(); }, []);
  const parseErr = (e) => {
    const msg = e?.message ?? "";
    // Only attempt to unwrap a {"error": "..."} envelope if the message
    // actually looks like a JSON object — otherwise plain client-side
    // errors (e.g. "Invalid JSON for name_translations") get mangled by
    // JSON.parse throwing a second, unrelated error that masks the real one.
    if (msg.trim().startsWith("{")) {
      try { return JSON.parse(msg).error || msg; } catch { /* fall through */ }
    }
    return msg;
  };
  const createAdmin = async () => {
    setError("");
    if (!form.first_name || !form.last_name || !form.email) { setError("Required fields missing."); return; }
    setLoading(true);
    try {
      const r = await req("/auth/create-admin", { method: "POST", body: JSON.stringify(form) });
      setSuccess(r.user);
      setForm({ first_name: "", last_name: "", email: "" });
      setShowForm(false);
      loadAdmins();
    } catch (e) { setError(parseErr(e)); }
    finally { setLoading(false); }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this admin? They will no longer be able to log in.")) return;
    setError("");
    try { await req(`/manage/admin/admins/${id}/deactivate`, { method: "POST" }); loadAdmins(); }
    catch (e) { setError(parseErr(e)); }
  };
  const reactivate = async (id) => {
    setError("");
    try { await req(`/manage/admin/admins/${id}/reactivate`, { method: "POST" }); loadAdmins(); }
    catch (e) { setError(parseErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this admin? This cannot be undone.")) return;
    setError("");
    try { await req(`/manage/admin/admins/${id}`, { method: "DELETE" }); loadAdmins(); }
    catch (e) { setError(parseErr(e)); }
  };
  return (
    <div>
      {success && <div style={{ background: "rgba(61,74,26,0.1)", border: "1px solid rgba(61,74,26,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#3D4A1A" }}>✓ Created: <strong>{success.full_name}</strong> ({success.email})</div>}
      {error && <div style={{ background: "rgba(139,26,47,0.08)", border: "1px solid rgba(139,26,47,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#8B1A2F" }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#1E2C14" }}>{admins.length} Admin{admins.length !== 1 ? "s" : ""}</span>
        <button onClick={() => { setShowForm(s => !s); setError(""); }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#8B1A2F", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          {showForm ? "✕ Cancel" : "+ Add admin"}
        </button>
      </div>
      {showForm && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E2C14", marginBottom: 14 }}>New Administrator</div>
          <div style={{ ...S.grid2, marginBottom: 12 }}>
            <div><label style={S.label}>First name *</label><input style={S.input} value={form.first_name} onChange={e => set("first_name", e.target.value)} /></div>
            <div><label style={S.label}>Last name *</label><input style={S.input} value={form.last_name} onChange={e => set("last_name", e.target.value)} /></div>
            <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Email *</label><input style={S.input} type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
          </div>
          <button onClick={createAdmin} disabled={loading} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: "#3D4A1A", color: "white", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            {loading ? "Creating…" : "Create admin"}
          </button>
        </div>
      )}
      {admins.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#6A7E58", fontSize: 13 }}>No admins yet</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #C4D0A8" }}>
            {["Name", "Email", "Status", "Created", ""].map((h, i) => (
              <th key={i} style={{ textAlign: "left", fontSize: 10, color: "#7A8E68", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", paddingBottom: 8 }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{admins.map((a, i) => {
            const active = a.is_active !== false;
            const isSelf = a.id === currentUserId;
            return (
              <tr key={i} style={{ borderBottom: "1px solid #E8EDE0" }}>
                <td style={{ padding: "10px 4px", fontSize: 13, color: "#2C3820", fontWeight: 500 }}>{a.full_name}{isSelf && <span style={{ fontSize: 11, color: "#886070", marginLeft: 6 }}>(you)</span>}</td>
                <td style={{ padding: "10px 4px", fontSize: 12, color: "#5A6E48" }}>{a.email}</td>
                <td style={{ padding: "10px 4px" }}>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 100, background: active ? "rgba(61,74,26,0.12)" : "rgba(139,26,47,0.1)", color: active ? "#3D4A1A" : "#8B1A2F" }}>
                    {active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: "10px 4px", fontSize: 11, color: "#6A7E58" }}>{a.created_at?.slice(0, 10)}</td>
                <td style={{ padding: "10px 0", whiteSpace: "nowrap" }}>
                  {isSelf ? (
                    <span style={{ fontSize: 11, color: "#8A9E78" }}>—</span>
                  ) : active ? (
                    <button onClick={() => deactivate(a.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#8B1A2F", cursor: "pointer" }}>Deactivate</button>
                  ) : (
                    <>
                      <button onClick={() => reactivate(a.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#3D4A1A", cursor: "pointer", marginRight: 6 }}>Reactivate</button>
                      <button onClick={() => remove(a.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #8B1A2F", background: "transparent", color: "#8B1A2F", cursor: "pointer" }}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}
// ── Dictionaries Tab ───────────────────────────────────────────────────────
function DictionariesTab() {
  const [dicts, setDicts] = useState(null);
  const [editingRoad, setEditingRoad] = useState(null);
  const [roadForm, setRoadForm] = useState({});

  useEffect(() => { req("/manage/admin/dictionaries").then(setDicts).catch(console.error); }, []);

  const saveRoad = async (id) => {
    try {
      await req(`/manage/admin/roads/${id}`, { method: "PUT", body: JSON.stringify(roadForm) });
      setEditingRoad(null);
      req("/manage/admin/dictionaries").then(setDicts);
    } catch (e) { alert("Failed: " + e.message); }
  };

  if (!dicts) return <div style={{ textAlign: "center", padding: 40, color: "#6A7E58" }}>Loading…</div>;

  return (
    <div>
      {/* Wine Roads */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1E2C14", marginBottom: 16 }}>🛤️ Wine Roads</div>
        {dicts.wine_roads?.map(road => (
          <div key={road.id} style={{ borderBottom: "1px solid #E8EDE0", paddingBottom: 16, marginBottom: 16 }}>
            {editingRoad === road.id ? (
              <div>
                <div style={{ marginBottom: 8 }}><label style={S.label}>Name</label><input style={S.input} value={roadForm.name || road.name} onChange={e => setRoadForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div style={{ marginBottom: 10 }}><label style={S.label}>Description</label><textarea style={{ ...S.input, minHeight: 60, resize: "vertical" }} value={roadForm.description || road.description || ""} onChange={e => setRoadForm(f => ({ ...f, description: e.target.value }))} /></div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => saveRoad(road.id)} style={{ padding: "7px 16px", borderRadius: 7, border: "none", background: "#3D4A1A", color: "white", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Save</button>
                  <button onClick={() => setEditingRoad(null)} style={{ padding: "7px 16px", borderRadius: 7, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer", fontSize: 12 }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1E2C14" }}>{road.name}</div>
                  <div style={{ fontSize: 12, color: "#6A7E58", marginTop: 2 }}>{road.slug}</div>
                  <div style={{ fontSize: 12, color: "#5A6E48", marginTop: 4 }}>{road.description || "—"}</div>
                </div>
                <button onClick={() => { setEditingRoad(road.id); setRoadForm({ name: road.name, description: road.description }); }}
                  style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer" }}>Edit</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Price Tiers */}
      <div style={S.card}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1E2C14", marginBottom: 16 }}>💰 Price Tiers</div>
        {dicts.price_tiers?.map(tier => (
          <div key={tier.value} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #E8EDE0" }}>
            <span style={{ fontSize: 16, minWidth: 40 }}>{"€".repeat(tier.value)}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#2C3820" }}>{tier.label}</div>
              <div style={{ fontSize: 12, color: "#6A7E58" }}>{tier.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Classificators Tab ─────────────────────────────────────────────────────
const CLASSIFICATOR_META = {
  wine_colours: "Colour",
  wine_co2_types: "CO2 Type (Still / Sparkling)",
  wine_sugar_types: "Sugar Type",
  wine_spirit_types: "Spirit Type",
  wine_special_types: "Special Type",
  wine_origin_control_levels: "Origin Control Level",
  wine_assemblage_types: "Assemblage Type",
  wine_price_ranges: "Price Range",
  grape_varieties: "Grape Variety",
};
const JSONB_FIELDS = new Set(["name_translations", "example_appellations", "applies_to_regions", "usd_range_hint", "aroma_profile"]);
const NUMERIC_FIELDS = new Set(["sort_order", "parent_id", "synonym_count", "institution_count", "vivc_number"]);

function ClassificatorsTab() {
  const [table, setTable] = useState("wine_colours");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({});

  const load = () => {
    setLoading(true); setError("");
    req(`/manage/admin/classificators/${table}`)
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); setEditingId(null); setShowNew(false); setNewForm({}); }, [table]);

  const parseErr = (e) => {
    const msg = e?.message ?? "";
    // Only attempt to unwrap a {"error": "..."} envelope if the message
    // actually looks like a JSON object — otherwise plain client-side
    // errors (e.g. "Invalid JSON for name_translations") get mangled by
    // JSON.parse throwing a second, unrelated error that masks the real one.
    if (msg.trim().startsWith("{")) {
      try { return JSON.parse(msg).error || msg; } catch { /* fall through */ }
    }
    return msg;
  };
  const fieldCols = rows.length > 0 ? Object.keys(rows[0]).filter(k => k !== "id") : [];

  const toDisplay = (col, val) => {
    if (JSONB_FIELDS.has(col)) return JSON.stringify(val ?? null);
    return val === null || val === undefined ? "" : String(val);
  };
  const fromDisplay = (col, str) => {
    if (JSONB_FIELDS.has(col)) {
      try { return (str ?? "").trim() === "" ? null : JSON.parse(str); }
      catch { throw new Error(`Invalid JSON for ${col}`); }
    }
    if (NUMERIC_FIELDS.has(col)) return (str ?? "").trim() === "" ? null : Number(str);
    if (col === "is_classic") return str === "true" || str === "1";
    return (str ?? "").trim() === "" ? null : str;
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    const f = {};
    fieldCols.forEach(c => { f[c] = toDisplay(c, row[c]); });
    setEditForm(f);
  };

  const saveEdit = async (id) => {
    setError("");
    try {
      const body = {};
      for (const c of fieldCols) body[c] = fromDisplay(c, editForm[c] ?? "");
      await req(`/manage/admin/classificators/${table}/${id}`, { method: "PUT", body: JSON.stringify(body) });
      setEditingId(null);
      load();
    } catch (e) { setError(parseErr(e)); }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    setError("");
    try { await req(`/manage/admin/classificators/${table}/${id}`, { method: "DELETE" }); load(); }
    catch (e) { setError(parseErr(e)); }
  };

  const openNew = () => {
    const f = {};
    fieldCols.forEach(c => { f[c] = JSONB_FIELDS.has(c) ? '{"en": ""}' : ""; });
    setNewForm(f);
    setShowNew(true);
  };

  const createRow = async () => {
    setError("");
    try {
      const body = {};
      for (const c of fieldCols) body[c] = fromDisplay(c, newForm[c] ?? "");
      await req(`/manage/admin/classificators/${table}`, { method: "POST", body: JSON.stringify(body) });
      setShowNew(false);
      load();
    } catch (e) { setError(parseErr(e)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <select style={{ ...S.input, width: "auto", minWidth: 260 }} value={table} onChange={e => setTable(e.target.value)}>
          {Object.entries(CLASSIFICATOR_META).map(([t, label]) => <option key={t} value={t}>{label} ({t})</option>)}
        </select>
        <button onClick={openNew} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#8B1A2F", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          + Add entry
        </button>
      </div>
      {error && <div style={{ background: "rgba(139,26,47,0.08)", border: "1px solid rgba(139,26,47,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#8B1A2F" }}>{error}</div>}
      {showNew && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1E2C14", marginBottom: 14 }}>New entry</div>
          <div style={{ display: "grid", gap: 10 }}>
            {fieldCols.map(c => (
              <div key={c}>
                <label style={S.label}>{c}{JSONB_FIELDS.has(c) ? " (JSON)" : ""}</label>
                {JSONB_FIELDS.has(c) ? (
                  <textarea style={{ ...S.input, minHeight: 60, fontFamily: "monospace", fontSize: 12 }} value={newForm[c] ?? ""} onChange={e => setNewForm(f => ({ ...f, [c]: e.target.value }))} />
                ) : (
                  <input style={S.input} value={newForm[c] ?? ""} onChange={e => setNewForm(f => ({ ...f, [c]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={createRow} style={{ padding: "8px 16px", borderRadius: 7, border: "none", background: "#3D4A1A", color: "white", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Create</button>
            <button onClick={() => setShowNew(false)} style={{ padding: "8px 16px", borderRadius: 7, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer", fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#6A7E58" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#6A7E58", fontSize: 13 }}>No entries</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #C4D0A8" }}>
            <th style={{ textAlign: "left", fontSize: 10, color: "#7A8E68", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", paddingBottom: 8 }}>ID</th>
            {fieldCols.map(c => (
              <th key={c} style={{ textAlign: "left", fontSize: 10, color: "#7A8E68", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", paddingBottom: 8 }}>{c}</th>
            ))}
            <th></th>
          </tr></thead>
          <tbody>{rows.map(row => (
            <tr key={row.id} style={{ borderBottom: "1px solid #E8EDE0" }}>
              <td style={{ padding: "10px 4px", fontSize: 12, color: "#6A7E58" }}>{row.id}</td>
              {fieldCols.map(c => (
                <td key={c} style={{ padding: "10px 4px", fontSize: 12, color: "#2C3820", maxWidth: 220 }}>
                  {editingId === row.id ? (
                    JSONB_FIELDS.has(c) ? (
                      <textarea style={{ ...S.input, minHeight: 44, fontFamily: "monospace", fontSize: 11 }} value={editForm[c] ?? ""} onChange={e => setEditForm(f => ({ ...f, [c]: e.target.value }))} />
                    ) : (
                      <input style={S.input} value={editForm[c] ?? ""} onChange={e => setEditForm(f => ({ ...f, [c]: e.target.value }))} />
                    )
                  ) : (
                    <span style={{ wordBreak: "break-word" }}>
                      {JSONB_FIELDS.has(c) ? (row[c]?.en || JSON.stringify(row[c])) : String(row[c] ?? "—")}
                    </span>
                  )}
                </td>
              ))}
              <td style={{ padding: "10px 0", whiteSpace: "nowrap" }}>
                {editingId === row.id ? (
                  <>
                    <button onClick={() => saveEdit(row.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#3D4A1A", cursor: "pointer", marginRight: 6 }}>Save</button>
                    <button onClick={() => setEditingId(null)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer" }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(row)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer", marginRight: 6 }}>Edit</button>
                    <button onClick={() => removeRow(row.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #8B1A2F", background: "transparent", color: "#8B1A2F", cursor: "pointer" }}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// ── Main Admin Page ────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password, role: "admin" }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Login failed");
      if (d.user?.role !== "admin") throw new Error("This login is for administrators only.");
      localStorage.setItem("wine_route_admin_token", d.token);
      onLogin(d.user);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 400, margin: "60px auto", padding: "0 16px" }}>
      <div style={{ background: "white", borderRadius: 12, padding: 32, border: "1px solid #C4D0A8" }}>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: "#1E2C14", margin: "0 0 24px" }}>Admin Login</h2>
        {error && <div style={{ background: "rgba(139,26,47,0.08)", color: "#8B1A2F", padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <div style={{ marginBottom: 14 }}><label style={S.label}>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} style={S.input} /></div>
        <div style={{ marginBottom: 20 }}><label style={S.label}>Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && login()} style={S.input} /></div>
        <div style={{ textAlign: "right", margin: "-10px 0 16px" }}>
          <Link to="/forgot-password" state={{ email, role: "admin" }} style={{ fontSize: 12, color: "#8B1A2F", textDecoration: "none" }}>Forgot password?</Link>
        </div>
        <button onClick={login} disabled={loading || !email || !password} style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "#8B1A2F", color: "white", fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: (loading || !email || !password) ? 0.6 : 1 }}>
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("stats");
  useEffect(() => {
    const token = localStorage.getItem("wine_route_admin_token");
    if (!token) { setChecking(false); return; }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (d.user?.role === "admin") setUser(d.user); else localStorage.removeItem("wine_route_admin_token"); })
      .catch(() => localStorage.removeItem("wine_route_admin_token"))
      .finally(() => setChecking(false));
  }, []);
  const signOut = () => { localStorage.removeItem("wine_route_admin_token"); setUser(null); };
  if (checking) return (
    <div style={S.page}>
      <TopBar title="Wine4Collect Admin" back={() => navigate("/")} />
      <div style={{ textAlign: "center", padding: 60, color: "#6A7E58" }}>Loading…</div>
    </div>
  );
  if (!user) return (
    <div style={S.page}>
      <TopBar title="Wine4Collect Admin" back={() => navigate("/")} />
      <AdminLogin onLogin={setUser} />
    </div>
  );
  const tabs = [
    { id: "stats", label: "📊 Stats" },
    { id: "reps", label: "🛤️ Road Reps" },
    { id: "admins", label: "👤 Admins" },
    { id: "dicts", label: "📚 Dictionaries" },
    { id: "classificators", label: "🍇 Classificators" },
  ];
  return (
    <div style={S.page}>
      <TopBar title="Wine4Collect Admin"
        right={<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#6A7E58" }}>{user.email}</span>
          <button onClick={signOut} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer" }}>Sign out</button>
        </div>}
        back={() => navigate("/")} />
      <div style={S.container}>
        <div style={{ display: "flex", gap: 6, marginBottom: 20, background: "white", padding: 6, borderRadius: 10, border: "1px solid #C4D0A8", width: "fit-content" }}>
          {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={S.tab(tab === t.id)}>{t.label}</button>)}
        </div>
        <div style={S.card}>
          {tab === "stats" && <StatsTab />}
          {tab === "reps" && <RepsTab />}
          {tab === "admins" && <AdminsTab currentUserId={user.id} />}
          {tab === "dicts" && <DictionariesTab />}
          {tab === "classificators" && <ClassificatorsTab />}
        </div>
      </div>
    </div>
  );
}
